import React, { useMemo, useRef } from 'react';

import {
  WidgetCnt,
  WidgetHeading,
} from 'pages/Dashboards/Widgets/widgets.style';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import TasksByAssigneeEmptyState from 'pages/Widgets/TasksByAssignee/EmptyRecords/TaskListWidgetEmptyState.cmp';
import WidgetActions from 'pages/Dashboards/Widgets/widgetActions';
import { useLoadWidget } from 'pages/Dashboards/Widgets/useLoadWidget';
import { exportToPng } from 'utils/globalFunction';
import { IWidgetData } from 'pages/Dashboards/interfaces';
import WidgetAdvanceFilter from 'pages/Dashboards/Widgets/WidgetAdvanceFilter/widgetAdvanceFilter';
import WithErrorState from '../errorStateCmp/errorState';
import WithLoader from '../withLoader';
import {
  useViewSubmissions,
  useViewTasks,
} from 'pages/Dashboards/hooks/useWidgets';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

interface IPieChartWidgetProps {
  widgetId: string;
  editable?: boolean;
  globalFilters?: any;
}

function PieChartWidget({
  widgetId,
  editable,
  globalFilters,
}: IPieChartWidgetProps) {
  const chartRef: any = useRef(null);
  const { parsedData, getWidgetQuery, widget } = useLoadWidget(
    widgetId,
    globalFilters,
  );

  const { handleViewTasks } = useViewTasks();
  const { handleViewSubmissions } = useViewSubmissions();
  const exportChartToImage = () => {
    exportToPng(chartRef.current, widget?.title);
  };
  const error = getWidgetQuery.error;
  const loading = getWidgetQuery.isFetching;
  const CustomLabel = ({
    cx,
    cy,
    value,
    innerRadius,
    outerRadius,
    midAngle,
    stroke,
    name,
    percent,
    valueX,
    ...rest
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) + 25;
    const cos = Math.cos(-RADIAN * midAngle);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percentage = `${(percent * 100).toFixed(2)}%`;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    return (
      <text
        cx={cx}
        cy={cy}
        stroke="none"
        x={x}
        y={y}
        alignmentBaseline="middle"
        className="recharts-text recharts-pie-label-text"
        textAnchor={textAnchor}
        fill={'#212121'}
      >
        <tspan x={x} dy="5px" fontSize={12}>
          {valueX}&nbsp;
          <tspan fontSize={12} fontWeight={600}>
            {value}
          </tspan>
          <tspan fontSize={12} fill={'#616161'}>
            &nbsp;({percentage})
          </tspan>
        </tspan>
      </text>
    );
  };
  const handleBarClick = (e: any) => {
    if (widget?.entity === 'task') {
      handleViewTasks(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
      );
    }

    if (widget?.entity === 'scheduledTask') {
      handleViewTasks(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
        true,
      );
    }

    if (widget?.entity == 'submission') {
      handleViewSubmissions(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
      );
    }
  };

  const isPercentage = useMemo(() => {
    return widget?.config?.variable?.includes('percentage');
  }, [widget]);

  return (
    <WidgetCnt ref={chartRef}>
      <WithLoader isLoading={loading}>
        <WithErrorState
          type={'pie'}
          error={error}
          reload={() => getWidgetQuery.refetch()}
        >
          <WidgetHeading>
            {widget?.title}
            <div className={'widgetActionsCnt'}>
              <WidgetAdvanceFilter
                widgetId={widgetId}
                globalFilters={globalFilters}
              />{' '}
              <WidgetActions
                exportChartToImage={exportChartToImage}
                widgetId={widgetId}
                editable={editable}
              />
            </div>
          </WidgetHeading>

          {parsedData?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart
                width={400}
                height={500}
                margin={{ top: 15, right: 15, left: 15, bottom: 15 }}
              >
                <Pie
                  dataKey="valueY"
                  onClick={handleBarClick}
                  isAnimationActive={false}
                  data={parsedData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label={CustomLabel}
                  minAngle={15}
                >
                  {parsedData.map((entry: IWidgetData, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'black', color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value) => [`${value}${isPercentage ? '%' : ''}`]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <TasksByAssigneeEmptyState />
          )}
        </WithErrorState>
      </WithLoader>
    </WidgetCnt>
  );
}

export default PieChartWidget;
